import { useQuery } from '@tanstack/react-query';

import { useAccessToken } from '@/services/auth';
import { StringMapBase } from '@/types';

import {
  equipmentByIdOptions,
  incubatorsOptions,
  stringMapOptions,
  veterinariansOptions,
} from '.';
import { IncubatorResponse, StringMapItem, TechnicianItem } from './types';

const SEMEN_TYPE_OBJECT_TYPE_CODE = 10017;

export function useIncubators(siteId: string | null | undefined) {
  const token = useAccessToken();
  const { data } = useQuery({
    ...incubatorsOptions(token, { siteId: siteId! }),
    enabled: !!token && !!siteId,
  });

  return data as IncubatorResponse[] | undefined;
}

export function useSemenTypes(): StringMapBase[] | undefined {
  const token = useAccessToken();
  const { data } = useQuery(
    stringMapOptions(token, {
      objectTypeCode: SEMEN_TYPE_OBJECT_TYPE_CODE,
      name: 'tog_sementype',
    }),
  );

  return data?.map((item) => ({
    key: Number(item.key),
    value: item.value,
  }));
}

export function useGetStringMapsQuery(params: {
  objectTypeCode: number;
  attributeName: string;
}): { data: StringMapItem[]; isLoading: boolean } {
  const token = useAccessToken();
  const { data, isLoading } = useQuery({
    ...stringMapOptions(token, {
      objectTypeCode: params.objectTypeCode,
      name: params.attributeName,
    }),
    enabled: !!token,
  });

  return {
    data: (data ?? []).map((item) => ({
      attributeValue: String(item.key),
      value: item.value,
    })),
    isLoading,
  };
}

export function useGetVeterinariansQuery(): {
  data: TechnicianItem[];
  isLoading: boolean;
} {
  const token = useAccessToken();
  const { data, isLoading } = useQuery({
    ...veterinariansOptions(token),
    enabled: !!token,
  });

  return {
    data: data ?? [],
    isLoading,
  };
}

export function useGetEquipmentByIdQuery(id: string): {
  data: TechnicianItem | null | undefined;
  isLoading: boolean;
} {
  const token = useAccessToken();
  const { data, isLoading } = useQuery({
    ...equipmentByIdOptions(token, id),
    enabled: !!token && !!id,
  });

  return { data, isLoading };
}
